import { useCallback, useState } from 'react';
import { FlatList, Pressable, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { mySubmissions } from '../api/submissions';
import { Submission } from '../types';
import { Button, Card, EmptyState, ErrorText, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { StatusBadge } from '../components/StatusBadge';
import { ApiError } from '../api/client';

const PAGE_SIZE = 50;

export default function ReviewerMySubmissionsScreen() {
  const { token } = useAuth();
  const router = useRouter();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await mySubmissions(token, 1, PAGE_SIZE);
      setSubmissions(result.submissions);
      setTotal(result.total);
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được danh sách bài nộp, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadMore = async () => {
    if (!token || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = Math.floor(submissions.length / PAGE_SIZE) + 1;
      const result = await mySubmissions(token, nextPage, PAGE_SIZE);
      setSubmissions((prev) => [...prev, ...result.submissions]);
      setTotal(result.total);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Không tải thêm được, vui lòng thử lại.');
    } finally {
      setLoadingMore(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  return (
    <ScreenContainer safeTop safeBottom>
      {loadError ? <ErrorText>{loadError}</ErrorText> : null}
      <FlatList
        data={submissions}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !loadError ? <EmptyState text="Bạn chưa nhận nhiệm vụ nào" /> : null}
        ListFooterComponent={
          submissions.length < total ? (
            <Button title="Xem thêm" variant="outline" onPress={loadMore} loading={loadingMore} />
          ) : null
        }
        renderItem={({ item }) => (
          <Pressable onPress={() => router.push(`/submission/${item.id}`)}>
            <Card>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text, flex: 1 }}>
                  {item.task?.title ?? 'Nhiệm vụ'}
                </Text>
                <StatusBadge status={item.status} />
              </View>
              <Text style={{ color: colors.textMuted, marginTop: 2 }}>
                {item.task?.shop?.name}
              </Text>
              {item.status === 'REJECTED' && item.rejectReason ? (
                <Text style={{ color: colors.danger, marginTop: 6, fontSize: 12 }}>
                  Lý do từ chối: {item.rejectReason}
                </Text>
              ) : null}
            </Card>
          </Pressable>
        )}
      />
    </ScreenContainer>
  );
}
